import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useCreateAgencyContactById } from '@nxsys/hooks/api/agency';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { showMessage } from '@nxsys/utils/message';
import { useRef } from 'react';
import AgencyContactForm, {
  AgencyContactFormRef,
  AgencyContactFormValues,
} from '../components/AgencyContactForm';
import { CreateAgencyContactParams } from '@nxsys/types/api';

type AddAgencyContactModalProps = {
  isOpen: boolean;
  toggle: () => void;
  agencyData: IAgency;
  refetchData: () => void;
};

const AddAgencyContactModal: React.FC<AddAgencyContactModalProps> = ({
  isOpen,
  toggle,
  agencyData,
  refetchData,
}) => {
  const { messages } = useTypedIntl();
  const agencyId = agencyData.id;

  const { run: createAgencyContactById, loading: isCreatingAgencyContact } =
    useCreateAgencyContactById();

  const formRef = useRef<AgencyContactFormRef>(null);
  const handleSubmit = async (values: AgencyContactFormValues) => {
    if (!agencyId) return;
    const params: CreateAgencyContactParams = {
      name: values.name,
      email: values.email,
      phone: values.phone || null,
      status: values.status?.value ?? null,
      companyRef: values.companyRef?.value ?? null,
      department: values.department,
    };
    createAgencyContactById(agencyId, params).then(() => {
      refetchData();
      toggle();
      showMessage({
        type: 'success',
        content: messages['common.createAgencyContactSuccess'],
      });
      formRef.current?.reset();
    });
  };

  return (
    <AppModalContainer
      title={messages['common.newAgencyContact']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.create'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isCreatingAgencyContact,
          disabled: isCreatingAgencyContact,
        },
      ]}
    >
      <AgencyContactForm formRef={formRef} onSubmit={handleSubmit} />
    </AppModalContainer>
  );
};

export default AddAgencyContactModal;
